import React, { useRef, useState } from "react";
import { UploadCloud, Trash2, ArrowUp, ArrowDown, GripVertical, Lock, Loader2, ImageOff } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { adminApi, uploadImage, imgUrl, errMsg } from "../lib/api";

function Item({ g, i, total, onMove, onPatch, onDelete, dnd }) {
  const [caption, setCaption] = useState(g.caption);
  const saveCaption = () => { if (caption !== g.caption) onPatch(g.id, { caption }); };
  return (
    <div
      draggable
      onDragStart={() => dnd.start(i)}
      onDragOver={(e) => { e.preventDefault(); dnd.over(i); }}
      onDrop={() => dnd.drop(i)}
      onDragEnd={dnd.end}
      className={`g-admin admin-card overflow-hidden ${dnd.from === i ? "dragging" : ""} ${dnd.to === i && dnd.from !== i ? "over" : ""}`}
      data-testid={`admin-gallery-item-${g.id}`}
    >
      <div className="relative aspect-[3/4] bg-[#f6eef1]">
        <img src={imgUrl(g.url)} alt={g.caption} className="w-full h-full object-cover" draggable={false} />
        <span className="absolute top-2 left-2 text-[11px] font-semibold bg-white/90 rounded-md px-2 py-0.5 flex items-center gap-1">
          <GripVertical className="w-3 h-3 text-[#a79fa3]" />#{i + 1}
        </span>
        {g.locked && (
          <span className="absolute top-2 right-2 text-[11px] font-semibold bg-[#ee3a70] text-white rounded-md px-2 py-0.5 flex items-center gap-1" data-testid="admin-locked-badge">
            <Lock className="w-3 h-3" /> VIP
          </span>
        )}
      </div>
      <div className="p-3 space-y-3">
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onBlur={saveCaption}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          placeholder="Add a caption…"
          className="w-full text-[13.5px] px-2.5 py-1.5 rounded-lg border border-transparent hover:border-[#efe3e7] focus:border-[#ee3a70] focus:outline-none focus:ring-2 focus:ring-[#ee3a70]/20"
          data-testid={`admin-caption-${g.id}`}
        />
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-[12.5px] text-[#4a4246] cursor-pointer">
            <Switch checked={g.locked} onCheckedChange={(v) => onPatch(g.id, { locked: v })}
              className="data-[state=checked]:bg-[#ee3a70]" data-testid={`admin-lock-${g.id}`} />
            VIP-locked
          </label>
          <div className="flex gap-1.5">
            <button className="icon-btn" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`admin-up-${g.id}`} aria-label="Move up"><ArrowUp className="w-4 h-4" /></button>
            <button className="icon-btn" disabled={i === total - 1} onClick={() => onMove(i, 1)} data-testid={`admin-down-${g.id}`} aria-label="Move down"><ArrowDown className="w-4 h-4" /></button>
            <button className="icon-btn danger" onClick={() => onDelete(g)} data-testid={`admin-delete-${g.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GalleryPanel({ gallery, setGallery, loading, refresh }) {
  const fileRef = useRef(null);
  const [uploads, setUploads] = useState([]);
  const [drag, setDrag] = useState(false);
  const [lockNew, setLockNew] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [dndState, setDnd] = useState({ from: null, to: null });

  const handleFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return toast.error("Please choose image files");
    const items = list.map((f) => ({ id: `${f.name}-${Date.now()}-${Math.random()}`, name: f.name, progress: 0 }));
    setUploads((u) => [...u, ...items]);
    let ok = 0;
    for (let k = 0; k < list.length; k++) {
      const it = items[k];
      try {
        const url = await uploadImage(list[k], (p) => setUploads((u) => u.map((x) => (x.id === it.id ? { ...x, progress: p } : x))));
        await adminApi.post("/admin/gallery", { url, caption: "", locked: lockNew });
        ok++;
      } catch (err) {
        toast.error(`${it.name}: ${errMsg(err, "upload failed")}`);
      } finally {
        setUploads((u) => u.filter((x) => x.id !== it.id));
      }
    }
    if (ok) { toast.success(`${ok} photo${ok > 1 ? "s" : ""} added`); await refresh(); }
  };

  const patch = async (id, body) => {
    setGallery((g) => g.map((x) => (x.id === id ? { ...x, ...body } : x)));
    try {
      await adminApi.patch(`/admin/gallery/${id}`, body);
      toast.success("Saved");
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Could not save"));
      refresh();
    }
  };

  const saveOrder = async (next) => {
    setGallery(next);
    try {
      await adminApi.put("/admin/gallery-order", { ids: next.map((g) => g.id) });
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Could not reorder"));
      refresh();
    }
  };
  const move = (i, d) => {
    const next = [...gallery];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    saveOrder(next);
  };

  const dnd = {
    ...dndState,
    start: (i) => setDnd({ from: i, to: i }),
    over: (i) => setDnd((s) => (s.to === i ? s : { ...s, to: i })),
    drop: (i) => {
      const { from } = dndState;
      if (from === null || from === i) return;
      const next = [...gallery];
      const [m] = next.splice(from, 1);
      next.splice(i, 0, m);
      saveOrder(next);
    },
    end: () => setDnd({ from: null, to: null }),
  };

  const confirmDelete = async () => {
    const g = toDelete;
    setToDelete(null);
    try {
      await adminApi.delete(`/admin/gallery/${g.id}`);
      toast.success("Photo deleted");
      refresh();
    } catch (err) {
      toast.error(errMsg(err, "Could not delete"));
    }
  };

  return (
    <div>
      <div
        className={`dropzone p-8 text-center cursor-pointer ${drag ? "drag" : ""}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (dndState.from === null) setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files); }}
        data-testid="admin-dropzone"
      >
        <UploadCloud className="w-10 h-10 mx-auto text-[#ee3a70]" />
        <p className="mt-3 text-[15px] font-medium text-[#1b1719]">Drop photos here or click to upload</p>
        <p className="text-[13px] text-[#7a7276] mt-1">JPG, PNG, WEBP or GIF · up to 20MB each · multiple allowed</p>
        <div className="mt-4 inline-flex items-center gap-2 text-[13px] text-[#4a4246]" onClick={(e) => e.stopPropagation()}>
          <Switch checked={lockNew} onCheckedChange={setLockNew} className="data-[state=checked]:bg-[#ee3a70]" data-testid="admin-lock-new" />
          Upload as VIP-locked
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="admin-gallery-input"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {uploads.length > 0 && (
        <div className="admin-card p-4 mt-4 space-y-3" data-testid="admin-upload-progress">
          {uploads.map((u) => (
            <div key={u.id} className="flex items-center gap-3">
              <Loader2 className="w-4 h-4 animate-spin text-[#ee3a70] shrink-0" />
              <span className="text-[13px] w-48 truncate">{u.name}</span>
              <Progress value={u.progress} className="h-2 flex-1" />
              <span className="text-[12px] text-[#7a7276] w-10 text-right">{u.progress}%</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-8 mb-4">
        <h2 className="text-[18px] font-semibold">Gallery <span className="text-[#a79fa3] font-normal">({gallery.length})</span></h2>
        <p className="text-[12.5px] text-[#7a7276]">Drag cards or use arrows to reorder · first 6 show on the landing page row</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="admin-card aspect-[3/4.6] animate-pulse bg-[#f6eef1]" />)}
        </div>
      ) : gallery.length === 0 ? (
        <div className="admin-card p-12 text-center text-[#7a7276]" data-testid="admin-gallery-empty">
          <ImageOff className="w-8 h-8 mx-auto" /><p className="mt-2 text-[14px]">No photos yet. Upload your first one above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="admin-gallery-grid">
          {gallery.map((g, i) => (
            <Item key={g.id} g={g} i={i} total={gallery.length} onMove={move} onPatch={patch} onDelete={setToDelete} dnd={dnd} />
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>It will be removed from the gallery and the landing page.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#dc2626] hover:bg-[#b91c1c]" data-testid="admin-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
